import React, { useState, useEffect } from "react";
import "../Forms/Forms.css";
import { useParams } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import { Snackbar, Alert as MuiAlert } from "@mui/material";
import axiosInstance from "../axiosinstance";

export default function ServiceMasterForm({ isSidebarExpanded }) {
  // State for form data (all fields stored in a single object)
  const { service_id } = useParams(); // Get the dynamic `client_id` from the URL
  console.log(service_id, "vishal");

  const [formData, setFormData] = useState({
    serviceName: "",
    purchaseCost: "",
    sellingCost: "",
    gst: "",
  });

  // State for error and success handling
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const [formErrors, setFormErrors] = useState({
    serviceName: "",
    purchaseCost: "",
    sellingCost: "",
    gst: "",
  });

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success"); // success or error

  useEffect(() => {
    if (!service_id) {
      console.error("Client ID is not available");
      return; // Prevent API call if client_id is invalid
    }

    const fetchFirmDetails = async () => {
      try {
        setIsLoading(true);

        const response = await axiosInstance.get(
          `/api/get_service_by_id/${service_id}`
        );
        setFormData({
          serviceName: response.data.result.service_name,
          purchaseCost: response.data.result.purchase_cost,
          sellingCost: response.data.result.selling_cost,
          gst: response.data.result.gst_rate,
        });
        setIsLoading(false);
      } catch (error) {
        setIsLoading(false);
        setError("Failed to load service details.");
        console.error("Error fetching service details:", error.message);
      }
    };

    fetchFirmDetails();
  }, [service_id]);

  // Handle form submission
  const handleSubmit = async (e) => {
    if (!validateForm()) {
      return; // If validation fails, stop the submission
    }
    e.preventDefault();

    // Prepare the data to send
    const serviceData = {
      service_name: formData.serviceName,
      purchase_cost: formData.purchaseCost,
      selling_cost: formData.sellingCost,
      gst_rate: formData.gst,
    };

    try {
      // Send data to the API using PUT method for updating an existing service
      const response = await axiosInstance.put(
        `/api/edit_service/${service_id}`,
        serviceData
      );
      if (response.status === 200) {
        setSnackbarMessage("Service updated successfully!");
        setSnackbarSeverity("success");
        setSnackbarOpen(true);
        setError(null);
        setTimeout(() => {
          navigate("/service-master");
        }, 2000); // Clear any previous error
      }
    } catch (err) {
      setSuccessMessage(null); // Clear any previous success message
      if (err.response) {
        // If server responded with an error
        setError(err.response.data.message || "Something went wrong.");
      } else {
        // If there's a network error
        setError("Network error, please try again later.");
      }
    }
  };

  // Handle cancel action
  const handleCancel = () => {
    window.history.back();
  };
  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  // Handle form input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });

    setFormErrors({
      ...formErrors,
      [name]: "",
    });
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.serviceName) {
      errors.serviceName = "Service Name is required.";
    }

    if (!formData.purchaseCost) {
      errors.purchaseCost = "Purchase Cost is required.";
    } else if (isNaN(formData.purchaseCost) || formData.purchaseCost <= 0) {
      errors.purchaseCost = "Purchase Cost must be a positive number.";
    }

    if (!formData.sellingCost) {
      errors.sellingCost = "Selling Cost is required.";
    } else if (isNaN(formData.sellingCost) || formData.sellingCost <= 0) {
      errors.sellingCost = "Selling Cost must be a positive number.";
    }

    if (!formData.gst) {
      errors.gst = "GST is required.";
    } else if (isNaN(formData.gst) || formData.gst < 0 || formData.gst > 100) {
      errors.gst = "GST must be a number between 0 and 100.";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0; // Return true if no errors
  };

  return (
    <div
      className={`table-container service-master-form-page ${
        isSidebarExpanded ? "expanded" : "collapsed"
      }`}
    >
      <div className="table-container-content form-main-body service-master-form-content">
        <div className="service-master-page-heading">
          <h1 className="form-title jost-bold">Edit Service Master</h1>
          <p className="service-master-page-subtitle">
            Update service pricing and applicable GST details.
          </p>
        </div>
        <div className="service-master-form-card">
        <div className="form-body service-master-form-body">
          <form onSubmit={handleSubmit} className="form-box service-master-form-box">
            <div className="form-body-container service-master-form-sections">
              <section className="service-master-section service-master-section-last">
                <div className="service-master-section-header">
                  <div>
                    <h2 className="service-master-section-title">
                      Service Details
                    </h2>
                    <p className="service-master-section-description">
                      Review and update the saved pricing configuration.
                    </p>
                  </div>
                </div>
              <div className="form-group">
                <label className="form-label jost-regular">
                  Service Name<span className="required">*</span>
                </label>
                <input
                  type="text"
                  className="input-field"
                  placeholder="Enter Service Name"
                  name="serviceName"
                  value={formData.serviceName}
                  onChange={handleChange}
                />
                {formErrors.serviceName && (
                  <div className="error-message">{formErrors.serviceName}</div>
                )}
              </div>

              <div className="form-group">
                <label className="form-label jost-regular">
                  Purchase Cost <span className="required">*</span>
                </label>
                <input
                  type="text"
                  className="input-field"
                  placeholder="Enter Purchase Cost"
                  name="purchaseCost"
                  value={formData.purchaseCost}
                  onChange={handleChange}
                />
                {formErrors.purchaseCost && (
                  <div className="error-message">{formErrors.purchaseCost}</div>
                )}
              </div>

              <div className="form-group">
                <label className="form-label jost-regular">
                  Selling Cost <span className="required">*</span>
                </label>
                <input
                  type="text"
                  className="input-field"
                  placeholder="Enter Selling Cost"
                  name="sellingCost"
                  value={formData.sellingCost}
                  onChange={handleChange}
                />
                {formErrors.sellingCost && (
                  <div className="error-message">{formErrors.sellingCost}</div>
                )}
              </div>

              <div className="form-group">
                <label className="form-label jost-regular">
                  GST in % <span className="required">*</span>
                </label>
                <input
                  type="text"
                  className="input-field"
                  placeholder="Enter GST in %"
                  name="gst"
                  value={formData.gst}
                  onChange={handleChange}
                />
                {formErrors.gst && (
                  <div className="error-message">{formErrors.gst}</div>
                )}
              </div>

              {/* Display success or error message */}
            </section>
            </div>
          </form>
        </div>
        <div className="form-footer">
          <div className="button-group">
            <button
              type="button"
              className="btn btn-cancel"
              onClick={handleCancel}
            >
              CANCEL
            </button>
            <button
              type="submit"
              className="btn btn-save"
              onClick={handleSubmit}
            >
              SAVE
            </button>
          </div>
        </div>
        </div>
      </div>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        style={{ transform: "translateY(40px)", marginLeft: "15px" }}
      >
        <MuiAlert onClose={handleSnackbarClose} severity="success">
          {snackbarMessage}
        </MuiAlert>
      </Snackbar>
    </div>
  );
}
